import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { Box } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { countries } from "../../../../components/Countries";
import { animateSelectStyle } from "../../../../styles/select";
import {
  CountryCode,
  PhNoFormat,
  PhNoWhiteListedCountries,
  WhiteListedCountries,
} from "../../../../utils/constants";
import FlagById from "../../../../components/FlagById";
import { KeyboardArrowDown } from "@mui/icons-material";
import { theme } from "../../../../components/Theme";
import PhoneInput from "react-phone-input-2";
import { BusinessSessionHelper } from "../../../../helpers/business-session.helper";
import startsWith from "lodash.startswith";
import { matchesFormat } from "../../../../utils/misc";
import { useEffect } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { z } from "zod";
import LoadingModal from "../../../../components/LoadingModal/LoadingModal";
import SigninForm from "./Signin/SigninForm";
import useFormContainer from "../../useFormContainer";

type Props = {
  formContainer: ReturnType<typeof useFormContainer>;
};

const cartSignupSchema = z.object({
  phoneNumber: z.string().optional(),
  phoneNumberCountryCode: z.coerce
    .number()
    .transform((num) => num.toString())
    .refine((str) => str.length >= 1, {
      message: "Phone number country code invalid",
    })
    .optional(),
  fullName: z.string().min(5),
  companyCountryCode: z.string().min(2),
  companyName: z.string().min(5).optional(),
  address: z.object({
    title: z.string().min(3),
    fullName: z.string().min(5),
    address: z.string().min(10),
    address2: z.string().min(10).optional(),
    countryCode: z.string().min(2),
    postalCode: z.string().optional(),
    province: z.string().min(5),
    phoneNumber: z.string().optional(),
    phoneNumberCountryCode: z.coerce
      .number()
      .transform((num) => num.toString())
      .refine((str) => str.length >= 1, {
        message: "Phone number country code invalid",
      })
      .optional(),
  }),
});
type CartSignupSchema = z.infer<typeof cartSignupSchema>;

const ShippingForm = ({ formContainer }: Props) => {
  const { t } = useTranslation();
  // const [searchParam, setSearchParams] = useSearchParams();
  const formMethods = useFormContext<CartSignupSchema>();
  const {
    register,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
    getValues,
  } = formMethods;

  const addressWatchFields = watch([
    "address.title",
    "address.fullName",
    "address.countryCode",
    "address.address",
    "address.province",
  ]);

  // const mutationPhone = useMutation({
  //   mutationFn: AuthService.verifyPhoneNumber,
  //   onSuccess: ({ data }) => {
  //     if (!data.isRegisteredPhone) {
  //       localStorage.setItem("method", "signup-phone");
  //       setShowPhone(false);
  //       setShowPhoneCompleted(true);
  //       setShowShippingAddress(true);
  //     } else {
  //       setIsValidPhone(false);
  //       setError(
  //         "phoneNumber",
  //         {
  //           message: t("commons.error.form.mobileNotAvailable"),
  //           type: "manual",
  //         },
  //         { shouldFocus: true }
  //       );
  //     }
  //     setIsLoading(false);
  //   },
  //   onError: (error: any) => {
  //     setIsLoading(false);
  //     toast(showErrorToast(error), ErrorToastConfig);
  //   },
  // });

  const {
    handleSubmitPhone,
    handleSubmitAddress,
    isLoading,
    isValidPhone,
    setIsValidPhone,
    showPhoneCompleted,
    setShowPhoneCompleted,
    showPhone,
    setShowPhone,
    showShippingAddress,
    showSignIn,
    setShowSignIn,
    setIsValidAddress,
    handleGoToNext,
    isValidAddress,
  } = formContainer;

  const validateAddressFields = () => {
    let result = false;
    const fieldsToValidate = getValues([
      "address.title",
      "address.fullName",
      "address.countryCode",
      "address.address",
      "address.province",
    ]);
    result =
      !fieldsToValidate.some((field) => field === undefined) &&
      !fieldsToValidate.includes("");
    return result;
  };

  useEffect(() => {
    setIsValidAddress(validateAddressFields());
  }, [addressWatchFields]);

  return (
    <>
      <Stack
        direction={"column"}
        sx={{
          gap: "16px",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        {/* Contact information complete alert */}
        {showPhoneCompleted && (
          <Sheet
            sx={{
              flex: 1,
              p: "16px",
              border: `1px solid ${theme.palette.neutral[300]}`,
              borderRadius: "8px",
            }}
          >
            <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
              <CheckCircleIcon
                width={26}
                color="var(--joy-palette-primary-500)"
              />
              <Typography
                display={"flex"}
                alignItems={"center"}
                level="text-md-semibold"
                textColor={theme.palette.others.icon_outline}
              >
                {t("catalog.checkout.contactInfo.title")}
              </Typography>
            </Box>
          </Sheet>
        )}

        {/* Contact information */}
        {showPhone && (
          <Sheet
            sx={{
              flex: 1,
              p: "16px",
              border: `1px solid ${theme.palette.neutral[300]}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              level="title-md"
              textColor={theme.palette.primary[500]}
              sx={{ mb: "16px" }}
            >
              {t("catalog.checkout.contactInfo.title")}
            </Typography>
            <Box>
              <Stack direction={"column"} spacing={{ xs: 1, sm: 2 }}>
                <FormControl
                  sx={{ width: "100%" }}
                  error={!!errors.phoneNumber}
                >
                  <FormLabel>{t("profile.address.phoneNumber")}*</FormLabel>
                  <PhoneInput
                    value={
                      (watch("phoneNumber") &&
                        `+${watch("phoneNumberCountryCode")}${watch(
                          "phoneNumber"
                        )}`) ||
                      ""
                    }
                    onlyCountries={PhNoWhiteListedCountries}
                    enableTerritories={false}
                    country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                    masks={{
                      my: "..-....-....",
                      sg: "....-....",
                      th: "... ... ...",
                      id: "... ... ... ...",
                      mm: ". .... .... .....",
                      au: "... ... ...",
                    }}
                    isValid={(inputNumber, meta: any, countries) => {
                      if (inputNumber === "" || inputNumber === meta.dialCode) {
                        setIsValidPhone(false);
                        return true;
                      }

                      if (
                        inputNumber.toString().startsWith(`${meta.dialCode}0`)
                      ) {
                        if (!errors.phoneNumber?.message) {
                          setError("phoneNumber", {
                            message: t(
                              "commons.error.form.mobileInvalidStartZero"
                            ),
                          });
                        }
                        setIsValidPhone(false);
                        return false;
                      }

                      const result = (countries || []).some((country: any) => {
                        if (
                          startsWith(inputNumber, country.dialCode) ||
                          startsWith(country.dialCode, inputNumber)
                        ) {
                          const iso2: CountryCode = meta.iso2 as CountryCode;
                          const format = PhNoFormat[iso2];
                          if (!format) {
                            setIsValidPhone(false);
                            return false;
                          }
                          const match = matchesFormat(
                            inputNumber,
                            format,
                            country.countryCode
                          );
                          setIsValidPhone(match);
                          return match;
                        }
                        setIsValidPhone(false);
                        return false;
                      });

                      if (!result && !errors.phoneNumber?.message) {
                        setError("phoneNumber", {
                          message: t("commons.error.form.mobileInvalid"),
                        });
                        setIsValidPhone(false);
                      } else if (result && !!errors.phoneNumber?.message) {
                        if (errors.phoneNumber.type === "manual") {
                          return false;
                        }
                        clearErrors("phoneNumber");
                        trigger("phoneNumber");
                        setIsValidPhone(true);
                      }
                      return result;
                    }}
                    inputStyle={{
                      width: "100%",
                      fontSize: "16px",
                      minHeight: "2.8rem",
                      height: "auto",
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                    buttonStyle={{
                      borderRadius: "8px 0 0 8px",
                    }}
                    onChange={(phone, meta: any) => {
                      setValue("phoneNumberCountryCode", meta.dialCode);
                      setValue(
                        "phoneNumber",
                        phone.replace(new RegExp(meta.dialCode), "")
                      );
                    }}
                  />
                  <Typography
                    level="text-xs-regular"
                    textColor={theme.palette.neutral[600]}
                    sx={{ mt: "8px" }}
                  >
                    {t("catalog.checkout.contactInfo.prompt")}
                  </Typography>
                  {errors.phoneNumber?.message && (
                    <FormHelperText>
                      {errors.phoneNumber?.message}
                    </FormHelperText>
                  )}
                </FormControl>
                <Stack
                  direction={{ xs: "column-reverse", md: "row" }}
                  spacing={2}
                  mt={2}
                  justifyContent={"space-between"}
                >
                  <Stack direction={"column"} justifyContent={"center"}>
                    <Stack
                      direction={"row"}
                      justifyContent={{ xs: "center", md: "flex-start" }}
                    >
                      <Typography
                        level="text-sm-regular"
                        textColor={theme.palette.neutral[600]}
                      >
                        {t("landing.signup.alreadyHaveAccount")}
                      </Typography>
                      <Typography
                        level="text-sm-semibold"
                        textColor={theme.palette.primary[500]}
                        ml={1}
                        sx={{
                          cursor: "pointer",
                          transition: "color 0.3s ease",
                          "&:hover": {
                            color: theme.palette.primary[700],
                          },
                          "&:active": {
                            color: theme.palette.primary[900],
                          },
                        }}
                        onClick={() => {
                          setShowPhone(false);
                          setShowPhoneCompleted(false);
                          setShowSignIn(true);
                          // setSearchParams();
                        }}
                      >
                        {t("landing.loginBtn")}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Button
                    className="h-10"
                    onClick={handleSubmitPhone}
                    disabled={!isValidPhone}
                  >
                    {t("commons.nextBtn")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Sheet>
        )}

        {/* SignIn  */}
        {showSignIn && <SigninForm handleGoToNext={handleGoToNext} />}

        {/* Shipping address alert */}
        {!showShippingAddress && (
          <Sheet
            sx={{
              background: theme.palette.neutral[50],
              flex: 1,
              p: "16px",
              border: `1px solid ${theme.palette.neutral[300]}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              level="title-md"
              textColor={theme.palette.neutral[400]}
              sx={{ mb: "16px" }}
            >
              {t("catalog.checkout.contactInfo.verify")}
            </Typography>
            <Typography
              level="text-sm-medium"
              textColor={theme.palette.neutral[400]}
            >
              {t("catalog.checkout.contactInfo.verifyPrompt")}
            </Typography>
          </Sheet>
        )}

        {/* Shipping address */}
        {showShippingAddress && (
          <Sheet
            sx={{
              flex: 1,
              p: "16px",
              border: `1px solid ${theme.palette.neutral[300]}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              level="title-md"
              textColor={theme.palette.primary[500]}
              sx={{ mb: "16px" }}
            >
              {t("catalog.checkout.shippingAddress")}
            </Typography>
            <Box>
              <Stack direction={"column"} spacing={{ xs: 1, sm: 2 }}>
                <FormControl error={!!errors.address?.title}>
                  <FormLabel>{t("profile.address.addressTitle")}*</FormLabel>
                  <Input
                    placeholder={t("profile.address.typeAddressTitle")}
                    sx={{ backgroundColor: "white" }}
                    {...register("address.title")}
                  />
                  {errors.address?.title?.message && (
                    <FormHelperText>
                      {errors.address?.title?.message}
                    </FormHelperText>
                  )}
                  <FormHelperText>
                    {"You can give title like ‘Home’, ‘Office’ or ‘Warehouse’"}
                  </FormHelperText>
                </FormControl>

                <FormControl error={!!errors.address?.fullName}>
                  <FormLabel>{t("profile.address.fullName")}*</FormLabel>
                  <Input
                    placeholder={t("profile.address.yourFullName")}
                    {...register("address.fullName")}
                  />
                  {errors.address?.fullName?.message && (
                    <FormHelperText>
                      {errors.address.fullName.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  sx={{ width: "100%" }}
                  error={!!errors.address?.countryCode}
                >
                  <FormLabel>{t("profile.address.country")}</FormLabel>
                  <Select
                    placeholder={t("profile.address.country")}
                    indicator={<KeyboardArrowDown />}
                    onChange={(_, value) => {
                      if (value) setValue("address.countryCode", value);
                    }}
                    renderValue={(option) => (
                      <Typography
                        startDecorator={
                          <FlagById id={`${option?.value ?? ""}`} />
                        }
                      >
                        {option?.label}
                      </Typography>
                    )}
                    value={watch("address.countryCode")}
                    sx={{ ...animateSelectStyle, backgroundColor: "white" }}
                  >
                    {countries
                      .filter((country) =>
                        WhiteListedCountries.includes(country.code)
                      )
                      .map((country) => (
                        <Option
                          key={country.code}
                          value={country.code}
                          label={country.label}
                        >
                          <Typography
                            startDecorator={<FlagById id={country.code} />}
                          >
                            {country.label}
                          </Typography>
                        </Option>
                      ))}
                  </Select>
                  {errors.address?.countryCode?.message && (
                    <FormHelperText>
                      {errors.address.countryCode.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl error={!!errors.companyName}>
                  <FormLabel>
                    {t(`landing.signup.companyName`, {
                      optional: `(${t("commons.optionalLabel")})`,
                    })}
                  </FormLabel>
                  <Input
                    placeholder={t(`landing.signup.companyName`, {
                      optional: `(${t("commons.optionalLabel")})`,
                    })}
                    {...register("companyName")}
                  />
                  {errors.companyName?.message && (
                    <FormHelperText>
                      {errors.companyName?.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl error={!!errors.address?.address}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine1")}*
                  </FormLabel>
                  <Input
                    placeholder={t("profile.address.typeAddress")}
                    {...register("address.address")}
                  />
                  {errors.address?.address?.message && (
                    <FormHelperText>
                      {errors.address.address.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl error={!!errors.address?.address2}>
                  <FormLabel>
                    {t("profile.editProfile.personalAddress.addressLine2")}
                  </FormLabel>
                  <Input
                    placeholder={t("profile.address.typeAddress")}
                    {...register("address.address2")}
                  />
                  {errors.address?.address2?.message && (
                    <FormHelperText>
                      {errors.address.address2.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                  <FormControl
                    sx={{ width: "100%" }}
                    error={!!errors.address?.province}
                  >
                    <FormLabel>{t("profile.address.provinceState")}*</FormLabel>
                    <Input
                      placeholder={t("profile.address.typeProvinceState")}
                      {...register("address.province")}
                    />
                    {errors.address?.province?.message && (
                      <FormHelperText>
                        {errors.address.province.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    sx={{ width: "100%" }}
                    error={!!errors.address?.postalCode}
                  >
                    <FormLabel>{t("profile.address.postalCode")}</FormLabel>
                    <Input
                      type="number"
                      placeholder={t("profile.address.typePostalCode")}
                      {...register("address.postalCode")}
                    />
                    {errors.address?.postalCode?.message && (
                      <FormHelperText>
                        {errors.address.postalCode.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>

                <Stack
                  direction={"row"}
                  spacing={2}
                  mt={2}
                  justifyContent={"end"}
                >
                  <Button
                    className="h-10"
                    onClick={handleSubmitAddress}
                    disabled={!isValidAddress}
                    sx={{
                      display: { xs: "none", md: "flex" },
                    }}
                  >
                    {t("commons.nextBtn")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Sheet>
        )}
      </Stack>
      <LoadingModal isLoading={isLoading} />
    </>
  );
};

export const ShippingFormSubmitButton = ({
  handleSubmitPhone,
  isValidPhone,
}: {
  handleSubmitPhone: () => void;
  isValidPhone: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Button
      fullWidth
      className="h-10"
      onClick={handleSubmitPhone}
      disabled={!isValidPhone}
    >
      {t("commons.nextBtn")}
    </Button>
  );
};

export default ShippingForm;
